/**
 * Shared mapping-value applier. The single source of truth that mutates a
 * CanvasElement after a mapping resolves to a value.
 *
 * Used by BOTH the export pipeline (brandResolver) and the data preview panel
 * (previewResolver) so a design surface, single export, and bulk export can
 * never disagree on how a resolved mapping is written into an element.
 *
 * Design rules:
 *   - Only touch the property named by `targetProperty`. Never mutate anything
 *     else on the element.
 *   - Color properties go through `normalizeCssColor()`. If the incoming value
 *     isn't a valid CSS color, skip the write silently so the element keeps
 *     its static color instead of rendering broken.
 *   - Numeric/boolean properties are parsed and clamped. Invalid parses skip
 *     the write (same silent-fallback rule).
 */

import type {
  CanvasElement,
  TextElement,
  ImageElement,
  ButtonElement,
  ShapeElement,
  PathElement,
  SvgElement,
  LineElement,
} from '../types/index';
import { replaceSvgColor, getSvgPrimaryFill } from './svgColorUtils';
import { computeFittedFontSize } from './textFitter';

/**
 * Apply shrink-to-fit to a text element whose fit mode is 'contain-within-textbox' or
 * 'contain-within-structure'. No-op for 'natural' (or undefined) so default behavior is unchanged.
 *
 * Safe to call multiple times — if the text already fits, fontSize is
 * untouched. Exported so the export pipeline can run a final sweep after
 * all mappings apply and fonts load.
 *
 * Returns the overflow verdict from the fitter (Spec §7): overflow=true
 * means the content does not fit the box even at the 8px floor. Callers
 * that only want the mutation may ignore the return value.
 */
export function applyTextFit(el: TextElement): { overflow: boolean } {
  const mode = el.textFitMode;
  if (mode !== 'contain-within-textbox' && mode !== 'contain-within-structure') return { overflow: false };
  if (!(el.width > 0) || !(el.height > 0) || !(el.fontSize > 0)) return { overflow: false };
  const result = computeFittedFontSize({
    text: el.text,
    boxWidth: el.width,
    boxHeight: el.height,
    fontSize: el.fontSize,
    fontFamily: el.fontFamily,
    fontWeight: el.fontWeight,
    fontStyle: el.fontStyle,
    lineHeight: el.lineHeight,
    letterSpacing: el.letterSpacing,
    minFontSize: 8,
  });
  if (result.shrunk) {
    el.fontSize = result.fittedFontSize;
  }
  return { overflow: result.overflow };
}

const CSS_NAMED_COLORS = new Set([
  'aliceblue','antiquewhite','aqua','aquamarine','azure','beige','bisque','black',
  'blanchedalmond','blue','blueviolet','brown','burlywood','cadetblue','chartreuse',
  'chocolate','coral','cornflowerblue','cornsilk','crimson','cyan','darkblue','darkcyan',
  'darkgoldenrod','darkgray','darkgreen','darkgrey','darkkhaki','darkmagenta','darkolivegreen',
  'darkorange','darkorchid','darkred','darksalmon','darkseagreen','darkslateblue','darkslategray',
  'darkslategrey','darkturquoise','darkviolet','deeppink','deepskyblue','dimgray','dimgrey',
  'dodgerblue','firebrick','floralwhite','forestgreen','fuchsia','gainsboro','ghostwhite',
  'gold','goldenrod','gray','green','greenyellow','grey','honeydew','hotpink','indianred',
  'indigo','ivory','khaki','lavender','lavenderblush','lawngreen','lemonchiffon','lightblue',
  'lightcoral','lightcyan','lightgoldenrodyellow','lightgray','lightgreen','lightgrey',
  'lightpink','lightsalmon','lightseagreen','lightskyblue','lightslategray','lightslategrey',
  'lightsteelblue','lightyellow','lime','limegreen','linen','magenta','maroon','mediumaquamarine',
  'mediumblue','mediumorchid','mediumpurple','mediumseagreen','mediumslateblue','mediumspringgreen',
  'mediumturquoise','mediumvioletred','midnightblue','mintcream','mistyrose','moccasin','navajowhite',
  'navy','oldlace','olive','olivedrab','orange','orangered','orchid','palegoldenrod','palegreen',
  'paleturquoise','palevioletred','papayawhip','peachpuff','peru','pink','plum','powderblue',
  'purple','rebeccapurple','red','rosybrown','royalblue','saddlebrown','salmon','sandybrown',
  'seagreen','seashell','sienna','silver','skyblue','slateblue','slategray','slategrey','snow',
  'springgreen','steelblue','tan','teal','thistle','tomato','transparent','turquoise','violet',
  'wheat','white','whitesmoke','yellow','yellowgreen',
]);

/**
 * Returns a normalized CSS color string or `null` if the input cannot be
 * interpreted as a color. Accepts `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`,
 * `rgb()`, `rgba()`, `hsl()`, `hsla()`, and CSS named colors. Leading/trailing
 * whitespace is trimmed. Case-insensitive.
 */
export function normalizeCssColor(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (trimmed.length === 0) return null;

  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
    return trimmed;
  }
  if (/^rgba?\(\s*[-\d.]+%?\s*,\s*[-\d.]+%?\s*,\s*[-\d.]+%?\s*(,\s*[-\d.]+%?\s*)?\)$/i.test(trimmed)) {
    return trimmed;
  }
  if (/^hsla?\(\s*[-\d.]+(deg|rad|turn)?\s*,\s*[-\d.]+%\s*,\s*[-\d.]+%\s*(,\s*[-\d.]+%?\s*)?\)$/i.test(trimmed)) {
    return trimmed;
  }
  if (CSS_NAMED_COLORS.has(trimmed.toLowerCase())) {
    return trimmed;
  }
  return null;
}

const COLOR_PROPERTIES = new Set<string>([
  'fontColor',
  'fillColor',
  'strokeColor',
]);

export function isColorProperty(targetProperty: string): boolean {
  return COLOR_PROPERTIES.has(targetProperty);
}

/**
 * Apply a resolved mapping value to `element`, mutating only the property
 * named by `targetProperty`. SVG and path color mappings are handled with
 * markup rewriting when applicable. Silent-fallback on invalid values.
 */
export function applyMappedValue(
  element: CanvasElement,
  targetProperty: string,
  value: string
): void {
  // SVG / path colors: rewrite markup or set color prop directly.
  if (
    (element.type === 'svg' || element.type === 'path') &&
    (targetProperty === 'fillColor' || targetProperty === 'strokeColor')
  ) {
    const normalized = normalizeCssColor(value);
    if (!normalized) return;
    const colorProp = targetProperty === 'fillColor' ? 'fill' : 'stroke';
    if (element.type === 'svg') {
      const svgEl = element as SvgElement;
      const duckColor = (svgEl as unknown as Record<string, unknown>)[colorProp] as
        | string
        | undefined;
      const currentColor =
        duckColor ??
        (colorProp === 'fill' ? getSvgPrimaryFill(svgEl) ?? undefined : undefined);
      if (svgEl.svgMarkup && currentColor) {
        svgEl.svgMarkup = replaceSvgColor(svgEl.svgMarkup, currentColor, normalized);
      }
      (svgEl as unknown as Record<string, unknown>)[colorProp] = normalized;
    } else {
      (element as PathElement)[colorProp] = normalized;
    }
    return;
  }

  switch (targetProperty) {
    case 'textContent':
      if (element.type === 'text') (element as TextElement).text = value;
      else if (element.type === 'button') (element as ButtonElement).text = value;
      break;
    case 'fontColor': {
      const color = normalizeCssColor(value);
      if (!color) return;
      if (element.type === 'text') {
        (element as TextElement).color = color;
        (element as TextElement).textFill = undefined;
      } else if (element.type === 'button') {
        (element as ButtonElement).color = color;
      }
      break;
    }
    case 'fontFamily':
      // Dynamic override of family invalidates any pinned variant id — the
      // stored variant pointed at the old family's postscript face.
      if (element.type === 'text') {
        (element as TextElement).fontFamily = value;
        (element as TextElement).fontVariantId = undefined;
      } else if (element.type === 'button') {
        (element as ButtonElement).fontFamily = value;
        (element as ButtonElement).fontVariantId = undefined;
      }
      break;
    case 'fontSize': {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        if (element.type === 'text') (element as TextElement).fontSize = num;
        else if (element.type === 'button') (element as ButtonElement).fontSize = num;
      }
      break;
    }
    case 'fontWeight': {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        // Weight change invalidates the pinned variant id; let the export
        // resolver re-match the closest face instead of using a stale postscript.
        if (element.type === 'text') {
          (element as TextElement).fontWeight = num;
          (element as TextElement).fontVariantId = undefined;
        } else if (element.type === 'button') {
          (element as ButtonElement).fontWeight = num;
          (element as ButtonElement).fontVariantId = undefined;
        }
      }
      break;
    }
    case 'textTransform':
      if (element.type === 'text') {
        const valid = ['none', 'uppercase', 'lowercase', 'capitalize'] as const;
        if (valid.includes(value as typeof valid[number])) {
          (element as TextElement).textTransform = value as TextElement['textTransform'];
        }
      }
      break;
    case 'textDecoration':
      if (element.type === 'text') {
        const valid = ['none', 'underline', 'line-through'] as const;
        if (valid.includes(value as typeof valid[number])) {
          (element as TextElement).textDecoration = value as TextElement['textDecoration'];
        }
      }
      break;
    case 'imageUrl':
    case 'imageSrc':
      if (element.type === 'image') (element as ImageElement).src = value;
      break;
    case 'fillColor': {
      const color = normalizeCssColor(value);
      if (!color) return;
      if (element.type === 'shape') (element as ShapeElement).fill = color;
      else if (element.type === 'path') (element as PathElement).fill = color;
      else if (element.type === 'line') (element as LineElement).stroke = color;
      break;
    }
    case 'strokeColor': {
      const color = normalizeCssColor(value);
      if (!color) return;
      if (element.type === 'shape') (element as ShapeElement).stroke = color;
      else if (element.type === 'path') (element as PathElement).stroke = color;
      else if (element.type === 'line') (element as LineElement).stroke = color;
      break;
    }
    case 'buttonLabel':
      if (element.type === 'button') (element as ButtonElement).text = value;
      break;
    case 'buttonHref':
      if (element.type === 'button') (element as ButtonElement).href = value;
      break;
    case 'buttonFillColor': {
      const color = normalizeCssColor(value);
      if (!color) return;
      if (element.type === 'button') (element as ButtonElement).backgroundColor = color;
      break;
    }
    case 'opacity': {
      const op = parseFloat(value);
      if (!isNaN(op)) element.opacity = Math.max(0, Math.min(1, op));
      break;
    }
    case 'visible':
      element.visible = value === 'true' || value === '1';
      break;
    default:
      if (targetProperty in element) {
        (element as unknown as Record<string, unknown>)[targetProperty] = value;
      }
      break;
  }
}
